import React from 'react'
import Styles from './BrandKitConfirm.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { BrandKitConfirmProps } from './Types'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight, Edit1 } from 'blaze-ui/icons'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'

import { FONT_PAIRINGS, FontPairing } from 'constants/fontPairings'
import { getBrandKitFontPairing } from 'hooks/queries/brandKits/getBrandKitFontPairing'
import { sample } from 'lodash'
import { AIJobStatus, BrandKit, FontRole } from 'types'

import { Heading } from 'blaze-ui/Heading/Heading'
import { PreviewCardModal, PreviewCardModalProps } from './PreviewCardModal/PreviewCardModal'
import { useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { useFetchBrandKitSync } from 'hooks/queries/useFetchBrandKitSync'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useBrandVisualEnhancement } from 'hooks/queries/brandVisualEnhancements/useBrandVisualEnhancement'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { LoadingAnimation } from './LoadingAnimation'
import { AddMoreImagesV2 } from '../AddMoreImages/AddMoreImagesV2'
import { useUpdateColorPalette } from 'hooks/queries/workspaces/brandVisualKits/useUpdateColorPalette'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { usePreviewState } from '../PreviewProvider/PreviewProvider'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { findClosestWeight } from 'flows/common/steps/BrandKitColorsAndFonts/BrandKitFontPicker/BrandKitFontPicker'
import { useRetryBrandKit } from 'hooks/queries/brandKits/useRetryBrandKit'
import cx from 'classnames'
import { BrandVisualEnhancement, BrandVisualEnhancementResult } from 'types'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'
import { Dropzone } from './Dropzone'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentImageLibrary } from 'hooks/useCurrentImageLibrary'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Logos } from './Logos/Logos'
import { useExperiment } from 'utils/experiments'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { setIsAddMoreImagesView } from './BrandKitConfirmContext'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'

// status check helpers
const isFinalStatus = (status: AIJobStatus) =>
  [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired].includes(status)

type EnhancementResponse = { brand_visual_enhancement: BrandVisualEnhancement } | null | undefined

const isSuccessfulEnhancement = (
  enhancement: EnhancementResponse,
): enhancement is { brand_visual_enhancement: BrandVisualEnhancement } =>
  enhancement?.brand_visual_enhancement?.status === AIJobStatus.Successful &&
  !!enhancement?.brand_visual_enhancement?.result

const getEnhancementResult = (enhancement: EnhancementResponse): BrandVisualEnhancementResult | null =>
  enhancement?.brand_visual_enhancement?.result || null

const getEnhancedColors = (enhancement: EnhancementResponse, selectedIndex: number = 0): string[] =>
  getEnhancementResult(enhancement)?.suggested_color_palettes?.[selectedIndex]?.colors || []

const shouldUseEnhanced = (userChoice: boolean | undefined, defaultValue: boolean = false) =>
  userChoice !== undefined ? userChoice : defaultValue

const resolveColors = (
  brandKit: BrandKit,
  enhancement: EnhancementResponse,
  useEnhanced: boolean,
  selectedIndex: number = 0,
): string[] => {
  if (useEnhanced && isSuccessfulEnhancement(enhancement)) {
    const enhancedColors = getEnhancedColors(enhancement, selectedIndex)
    if (enhancedColors.length > 0) return enhancedColors
  }
  return brandKit.brand_visual_kit?.primary_color_palette?.colors.map((c) => c.hex) || []
}

const getEnhancedFontPairing = (enhancement: EnhancementResponse, selectedIndex: number = 0): FontPairing | null => {
  const result = getEnhancementResult(enhancement)
  if (!result?.suggested_font_pairings?.[selectedIndex]) return null

  const { display_font: displayFont, body_font: bodyFont } = result.suggested_font_pairings[selectedIndex]

  const validWeights: FontWeightType[] = [100, 200, 300, 400, 500, 600, 700, 800, 900]
  const titleWeight = findClosestWeight(validWeights, displayFont.weight)
  const bodyWeight = findClosestWeight(validWeights, bodyFont.weight)

  return [
    {
      font_role: FontRole.Title,
      style: {
        font_family: displayFont.family,
        font_weight: titleWeight,
        line_height: 1.2,
        letter_spacing: 0,
      },
    },
    {
      font_role: FontRole.Body,
      style: {
        font_family: bodyFont.family,
        font_weight: bodyWeight,
        line_height: 1.5,
        letter_spacing: 0,
      },
    },
  ]
}

const resolveFontPairing = (
  brandKit: BrandKit,
  enhancement: EnhancementResponse,
  useEnhanced: boolean,
  selectedIndex: number = 0,
): FontPairing => {
  if (useEnhanced && isSuccessfulEnhancement(enhancement)) {
    const enhancedPairing = getEnhancedFontPairing(enhancement, selectedIndex)
    if (enhancedPairing) return enhancedPairing
  }

  const brandKitPairing = brandKit.brand_visual_kit
    ? (getBrandKitFontPairing(brandKit.brand_visual_kit) as FontPairing)
    : null

  return brandKitPairing || (sample(FONT_PAIRINGS) as FontPairing)
}

export const BrandKitConfirm = observer(
  ({
    onNext,
    onBack,
    brandKitId,
    isUsingEnhancedFonts,
    isUsingEnhancedColors,
    brandVisualEnhancementId,
    onNavigateToFonts,
    onNavigateToColors,
    brandProfileId,
    selectedEnhancedFontIndex,
    selectedEnhancedColorIndex,
    onEnhancementCreated,
    isReturningFromEdit,
  }: BrandKitConfirmProps) => {
    const {
      brandKit: previewBrandKit,
      setBrandKit: setPreviewBrandKit,
      setFontStyles,
      setColors,
      setMedia,
      colors: previewColors,
      fontStyles: previewFontStyles,
    } = usePreviewState()
    const isOnboardingRedesign = useIsOnboardingRedesign()
    const { isMobile } = useResponsiveDesign()
    const showAddMoreImages = useFlipperFlag('vm_onboarding_add_more_images')
    const addMoreImagesVariant = useExperiment('onboarding_add_more_images')

    const { fonts } = useRootStore()
    const { mutateAsync: retryBrandKit } = useRetryBrandKit({ id: brandKitId })
    useBrandProfile()

    const currentWorkspaceId = useCurrentWorkspaceId()
    const queryClient = useQueryClient()
    const { openModal } = useModals()
    const fetchBrandKit = useFetchBrandKitSync({
      brandKitId,
      includeAllMediaFiles: true,
      waitForBadImageDetection: true,
    })
    const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId })

    const brandKit = previewBrandKit
    const [isInitialized, setIsInitialized] = useState(false)
    const [loading, setLoading] = useState(false)
    const [onlyUseBrandKitMedia, setOnlyUseBrandKitMedia] = useState(
      brandKit?.content_preferences?.media_priority === 'only_my_brand_kit',
    )
    const { mutateAsync: updateColorPalette } = useUpdateColorPalette({
      brandVisualKitId: brandKit?.brand_visual_kit?.id as number,
      id: brandKit?.brand_visual_kit?.primary_color_palette.id as number,
    })
    const { mutateAsync: upsertTitleStyle } = useUpsertTextStyle({
      brandVisualKitId: brandKit?.brand_visual_kit?.id as number,
      fontRole: FontRole.Title,
    })
    const { mutateAsync: upsertBodyStyle } = useUpsertTextStyle({
      brandVisualKitId: brandKit?.brand_visual_kit?.id as number,
      fontRole: FontRole.Body,
    })

    const brandVisualKitId = brandKit?.brand_visual_kit?.id

    const computedBrandVisualEnhancementId = brandKit?.brand_visual_kit?.brand_visual_enhancement_id

    const { data: brandVisualEnhancement } = useBrandVisualEnhancement(
      brandVisualKitId,
      computedBrandVisualEnhancementId ?? undefined,
      {
        pollUntilFinal: true,
        enabled: !!computedBrandVisualEnhancementId && !!brandVisualKitId,
        keepPreviousData: true,
      },
    )
    const generationCalledRef = useRef(false)

    const enhancementStatus = brandVisualEnhancement?.brand_visual_enhancement.status

    const imageLibrary = useCurrentImageLibrary()
    const { uploadImageFiles, isUploading } = useImportMediaFiles(false, (files) => {
      if (!brandKit) return

      const newImages =
        files?.map((file) => ({
          id: file.id,
          source_url: file.source_url || '',
          mime_type: file.mime_type || '',
          variants: file.variants || [],
          asset_id: file.media_file_id,
        })) || []

      queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits'])
      queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', brandKitId])
      setPreviewBrandKit?.({ ...brandKit, media_files: [...newImages, ...brandKit.media_files] })
      queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', brandKitId, 'mediaFiles'])
      if (imageLibrary?.key) {
        queryClient.invalidateQueries(['workspace', currentWorkspaceId, 'files', imageLibrary.key])
      }
    })

    const handleRetry = useCallback(async () => {
      // reset one-shot guards so the normal "loading → polling → loaded" flow runs again
      generationCalledRef.current = false
      setIsInitialized(false)

      // hit the /retry endpoint
      await retryBrandKit()

      // immediately start a fresh polling cycle
      fetchBrandKit.mutateAsync({ signal: new AbortController().signal })
    }, [retryBrandKit, fetchBrandKit])

    const handleToggleBrandKitMedia = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const isEnabled = e.target.checked
        setOnlyUseBrandKitMedia(isEnabled)

        const mediaPriority = isEnabled ? 'only_my_brand_kit' : 'brand_kit_first'
        const currentPreferences = brandKit?.content_preferences || {}

        await updateBrandKit({
          content_preferences: {
            ...currentPreferences,
            media_priority: mediaPriority,
          },
        })
      },
      [brandKit?.content_preferences, updateBrandKit],
    )

    const [addMoreImagesCompleted, setAddMoreImagesCompleted] = useState(false)
    const [fontsAndColorsResolved, setFontsAndColorsResolved] = useState(false)
    const [selectedFontPairing, setSelectedFontPairing] = useState<FontPairing | undefined>()

    useEffect(() => {
      if (brandKit && brandKit.id !== previewBrandKit?.id) {
        setPreviewBrandKit(brandKit)
      }
    }, [brandKit, previewBrandKit, setPreviewBrandKit])

    useEffect(() => {
      if (brandKit?.content_preferences?.media_priority) {
        setOnlyUseBrandKitMedia(brandKit.content_preferences.media_priority === 'only_my_brand_kit')
      }
    }, [brandKit?.content_preferences?.media_priority])

    useEffect(() => {
      if (isReturningFromEdit) return
      setFontsAndColorsResolved(false)
      generationCalledRef.current = false
    }, [
      isUsingEnhancedFonts,
      isUsingEnhancedColors,
      selectedEnhancedFontIndex,
      selectedEnhancedColorIndex,
      isReturningFromEdit,
    ])

    // resolve fonts and colors once all dependencies are ready
    useEffect(() => {
      if (!brandKit || fontsAndColorsResolved) return

      if (isReturningFromEdit) {
        if (previewColors?.length > 0) setColors(previewColors)
        if (previewFontStyles && !selectedFontPairing) setSelectedFontPairing(previewFontStyles)
        setFontsAndColorsResolved(true)
        return
      }

      //initial load, check all dependencies are ready
      const needsEnhancement =
        (shouldUseEnhanced(isUsingEnhancedFonts) || shouldUseEnhanced(isUsingEnhancedColors)) &&
        computedBrandVisualEnhancementId
      const enhancementReady = needsEnhancement
        ? brandVisualEnhancement?.brand_visual_enhancement && enhancementStatus && isFinalStatus(enhancementStatus)
        : true

      if (!isFinalStatus(brandKit.status) || !fonts.ready || !enhancementReady) {
        return
      }

      const shouldUseEnhancedFonts = shouldUseEnhanced(isUsingEnhancedFonts)
      const shouldUseEnhancedColors = shouldUseEnhanced(isUsingEnhancedColors)

      const resolvedColors = resolveColors(
        brandKit,
        brandVisualEnhancement,
        shouldUseEnhancedColors,
        selectedEnhancedColorIndex || 0,
      )
      setColors(resolvedColors)
      const fontPairing = resolveFontPairing(
        brandKit,
        brandVisualEnhancement,
        shouldUseEnhancedFonts,
        selectedEnhancedFontIndex || 0,
      )
      setSelectedFontPairing(fontPairing)
      setFontStyles(fontPairing)
      if (brandKit.media_files?.[0]) setMedia(brandKit.media_files[0])
      setFontsAndColorsResolved(true)
    }, [
      brandKit,
      isUsingEnhancedFonts,
      isUsingEnhancedColors,
      brandVisualEnhancement,
      enhancementStatus,
      selectedEnhancedFontIndex,
      selectedEnhancedColorIndex,
      fonts.ready,
      fontsAndColorsResolved,
      setColors,
      setFontStyles,
      setPreviewBrandKit,
      setMedia,
      computedBrandVisualEnhancementId,
      showAddMoreImages,
      addMoreImagesVariant,
      isReturningFromEdit,
      previewColors,
      previewFontStyles,
      selectedFontPairing,
    ])

    useEffect(() => {
      if (!fontsAndColorsResolved || !getEnhancementResult(brandVisualEnhancement) || isReturningFromEdit) return

      const shouldUseEnhancedColors = shouldUseEnhanced(isUsingEnhancedColors)

      if (shouldUseEnhancedColors && isSuccessfulEnhancement(brandVisualEnhancement)) {
        const enhancedColors = getEnhancedColors(brandVisualEnhancement, selectedEnhancedColorIndex || 0)
        if (enhancedColors.length > 0) {
          setColors(enhancedColors)
        }
      }
    }, [
      fontsAndColorsResolved,
      brandVisualEnhancement,
      enhancementStatus,
      isUsingEnhancedColors,
      selectedEnhancedColorIndex,
      setColors,
      previewColors,
      selectedFontPairing,
      isReturningFromEdit,
    ])

    // Fetch brand kit until it's status is successful
    useEffect(() => {
      fetchBrandKit.mutateAsync(
        { signal: new AbortController().signal },
        {
          onSuccess: (data) => {
            setPreviewBrandKit(data)
            if (!isInitialized) {
              setIsInitialized(true)
            }
          },
        },
      )
    }, [setPreviewBrandKit]) // eslint-disable-line react-hooks/exhaustive-deps

    const shouldShowAddMoreImages =
      !isMobile &&
      showAddMoreImages &&
      !isReturningFromEdit &&
      brandKit &&
      brandKit.media_files.length < 30 &&
      addMoreImagesVariant === 'add_more_images'

    const showingAddMoreImages = shouldShowAddMoreImages && !addMoreImagesCompleted

    useEffect(() => {
      setIsAddMoreImagesView(!!showingAddMoreImages)
    }, [showingAddMoreImages])

    const handleBackFromMain = () => {
      if (shouldShowAddMoreImages) {
        setAddMoreImagesCompleted(false)
      } else {
        onBack()
      }
    }

    const handleOpenPreviewCardModal = useCallback(
      (props: Partial<PreviewCardModalProps>) => {
        if (!brandKit) return

        openModal(PreviewCardModal, { ...props, brandKit })
      },
      [openModal, brandKit],
    )

    const handleNext = async () => {
      if (!brandKit || (!isUsingEnhancedColors && !isUsingEnhancedFonts)) {
        onNext()
        return
      }

      setLoading(true)
      const tasks: Promise<unknown>[] = []

      // Persist enhanced colours
      if (isUsingEnhancedColors && isSuccessfulEnhancement(brandVisualEnhancement)) {
        const enhancedColors = getEnhancedColors(brandVisualEnhancement, selectedEnhancedColorIndex || 0)
        const currentColors = brandKit.brand_visual_kit?.primary_color_palette?.colors.map((c) => c.hex) || []
        if (JSON.stringify(enhancedColors) !== JSON.stringify(currentColors)) {
          tasks.push(
            updateColorPalette({
              colors: enhancedColors.map((hex: string) => ({ hex, name: undefined })),
            }),
          )
        }
      }

      if (isUsingEnhancedFonts && isSuccessfulEnhancement(brandVisualEnhancement)) {
        const enhancedPairing = getEnhancedFontPairing(brandVisualEnhancement, selectedEnhancedFontIndex || 0)
        if (enhancedPairing) {
          const [enhancedTitle, enhancedBody] = enhancedPairing
          const websitePairing = brandKit.brand_visual_kit ? getBrandKitFontPairing(brandKit.brand_visual_kit) : null

          if (
            websitePairing?.[0]?.style.font_family !== enhancedTitle.style.font_family ||
            websitePairing?.[0]?.style.font_weight !== enhancedTitle.style.font_weight
          ) {
            tasks.push(upsertTitleStyle({ style: enhancedTitle.style, name: enhancedTitle.style.font_family }))
          }
          if (
            websitePairing?.[1]?.style.font_family !== enhancedBody.style.font_family ||
            websitePairing?.[1]?.style.font_weight !== enhancedBody.style.font_weight
          ) {
            tasks.push(upsertBodyStyle({ style: enhancedBody.style, name: enhancedBody.style.font_family }))
          }
        }
      }

      if (tasks.length) {
        await Promise.all(tasks)
        await queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brandKits'])
        setLoading(false)
      }

      onNext()
    }

    const isBadImageDetectionReady = !showAddMoreImages || brandKit?.bad_image_detection_completed

    const isLoading = isReturningFromEdit
      ? false
      : !brandKit ||
        !isFinalStatus(brandKit.status) ||
        !fonts.ready ||
        !isBadImageDetectionReady ||
        fetchBrandKit.isLoading

    const title = "💥 BAM! Here's your Brand Kit"

    if (fetchBrandKit.isError || brandKit?.status === AIJobStatus.Failed) {
      return (
        <MultiStepStepLayout
          title={"Whoops!  We weren't able to generate your Brand Kit"}
          description={"We hit a snag while generating your Brand Kit. Let's try again."}
          scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
          contentClassName={VirtualMarketerOnboardingStyles.content}
        >
          <StickyFooterProvider
            footer={
              <StickyFooter>
                <FooterButtons>
                  <Button
                    variant="tertiary"
                    size={isOnboardingRedesign ? 'lg' : 'xl'}
                    onPress={onBack}
                    frontIcon={isOnboardingRedesign ? undefined : ArrowLeft}
                    aria-label="Back"
                  >
                    Back
                  </Button>
                  <Button
                    size={isOnboardingRedesign ? 'lg' : 'xl'}
                    onPress={handleRetry}
                    endIcon={isOnboardingRedesign ? undefined : ArrowRight}
                    aria-label="Continue"
                    isDisabled={loading}
                  >
                    {loading ? 'Retrying...' : 'Retry'}
                  </Button>
                </FooterButtons>
              </StickyFooter>
            }
          >
            <div />
          </StickyFooterProvider>
        </MultiStepStepLayout>
      )
    }

    if (isLoading) {
      return <LoadingAnimation handleBack={onBack} />
    }

    if (showingAddMoreImages) {
      return (
        <AddMoreImagesV2
          onNext={() => setAddMoreImagesCompleted(true)}
          onBack={onBack}
          brandKitId={brandKitId}
          onSuccess={(newMediaFiles) => {
            if (brandKit) {
              setPreviewBrandKit({
                ...brandKit,
                media_files: [...newMediaFiles, ...brandKit.media_files],
              })
            }
          }}
        />
      )
    }

    const titleFont = selectedFontPairing?.[0]?.style.font_family
      ? fonts.get(selectedFontPairing[0].style.font_family)
      : null
    const bodyFont = selectedFontPairing?.[1]?.style.font_family
      ? fonts.get(selectedFontPairing[1].style.font_family)
      : null

    const titleFontWeight =
      (titleFont?.weights?.length ?? 0) > 0
        ? findClosestWeight(titleFont?.weights ?? [], selectedFontPairing?.[0]?.style.font_weight ?? 700)
        : 700

    const bodyFontWeight =
      (bodyFont?.weights?.length ?? 0) > 0
        ? findClosestWeight(bodyFont?.weights ?? [], selectedFontPairing?.[1]?.style.font_weight ?? 400)
        : 400

    const getFontNameLength = (fontName: string | undefined): 'short' | 'medium' | 'long' | undefined => {
      if (!fontName) return undefined
      if (fontName.length < 15) return 'short'
      if (fontName.length < 25) return 'medium'
      return 'long'
    }

    return (
      <MultiStepStepLayout
        title={title}
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
        contentClassName={cx(VirtualMarketerOnboardingStyles.content, Styles.contentWrapper)}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <ResponsiveDesign.Desktop>
                  <Button
                    id="brand-kit-confirm-back"
                    variant="tertiary"
                    size={isOnboardingRedesign ? 'lg' : 'xl'}
                    onPress={handleBackFromMain}
                    frontIcon={isOnboardingRedesign ? undefined : ArrowLeft}
                    aria-label="Back"
                    isDisabled={loading}
                  >
                    Back
                  </Button>
                </ResponsiveDesign.Desktop>
                <Button
                  id="brand-kit-confirm-next"
                  size={isOnboardingRedesign ? 'lg' : 'xl'}
                  onPress={handleNext}
                  endIcon={isOnboardingRedesign ? undefined : ArrowRight}
                  aria-label="Continue"
                  isDisabled={loading}
                  fullWidth={isMobile}
                >
                  {loading ? 'Saving...' : 'Continue'}
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          {selectedFontPairing?.[0]?.style.font_family && (
            <>
              <GoogleFontLoader family={selectedFontPairing[0].style.font_family} />
              <BlazeFontLoader name={selectedFontPairing[0].style.font_family} />
            </>
          )}
          {selectedFontPairing?.[1]?.style.font_family && (
            <>
              <GoogleFontLoader family={selectedFontPairing[1].style.font_family} />
              <BlazeFontLoader name={selectedFontPairing[1].style.font_family} />
            </>
          )}

          <div className={Styles.root}>
            <div className={Styles.logoColorsFontsContainer}>
              <div className={Styles.sectionContainer}>
                <div className={Styles.header}>
                  <div className={Styles.titleContainer}>
                    <span className={Styles.heading}>Logo</span>
                  </div>
                </div>
                <div className={Styles.contents}>
                  {brandKit?.brand_visual_kit?.id && <Logos brandVisualKitId={brandKit.brand_visual_kit.id} />}
                </div>
              </div>

              <div className={Styles.colorsAndFontsContainer}>
                <div className={cx(Styles.colorsContainer, Styles.sectionContainer)}>
                  <div className={Styles.header}>
                    <div className={Styles.titleContainer}>
                      <span className={Styles.heading}>Social Post Colors</span>
                    </div>
                    <Button
                      id="brand-kit-confirm-edit-colors"
                      variant="ghost"
                      size="sm"
                      aria-label="Edit colors"
                      frontIcon={Edit1}
                      onPress={() =>
                        brandKit &&
                        brandVisualEnhancement?.brand_visual_enhancement &&
                        onNavigateToColors(brandKit, brandVisualEnhancement.brand_visual_enhancement)
                      }
                      isDisabled={!brandKit || !brandVisualEnhancement?.brand_visual_enhancement}
                    >
                      Edit
                    </Button>
                  </div>
                  <div className={Styles.content}>
                    <div className={Styles.paletteContainer}>
                      {previewColors && previewColors.length > 0
                        ? previewColors.map((color, index) => (
                            <div
                              key={`${color}-${index}`}
                              className={Styles.colorSwatch}
                              style={{ backgroundColor: color }}
                            />
                          ))
                        : Array.from({ length: 5 }).map((_, index) => (
                            <div key={`skeleton-${index}`} className={`${Styles.colorSwatch} ${Styles.placeholder}`} />
                          ))}
                    </div>
                  </div>
                </div>
                <div className={cx(Styles.fontsContainer, Styles.sectionContainer)}>
                  <div className={Styles.header}>
                    <div className={Styles.titleContainer}>
                      <span className={Styles.heading}>Social Post Fonts</span>
                    </div>
                    <Button
                      id="brand-kit-confirm-edit-fonts"
                      variant="ghost"
                      size="sm"
                      aria-label="Edit fonts"
                      frontIcon={Edit1}
                      onPress={() =>
                        brandKit &&
                        brandVisualEnhancement?.brand_visual_enhancement &&
                        onNavigateToFonts(brandKit, brandVisualEnhancement.brand_visual_enhancement)
                      }
                      isDisabled={!brandKit || !brandVisualEnhancement?.brand_visual_enhancement}
                    >
                      Edit
                    </Button>
                  </div>
                  <div className={Styles.content}>
                    <div className={Styles.fontsGrid}>
                      <Text variant="label" color="var(--dark-40)">
                        Title
                      </Text>
                      <Text variant="label" color="var(--dark-40)">
                        Body
                      </Text>
                      <div
                        className={cx(Styles.fontSelectorLabel, { [Styles.placeholder]: !selectedFontPairing?.[0] })}
                        data-length={getFontNameLength(selectedFontPairing?.[0]?.style.font_family)}
                        style={
                          selectedFontPairing?.[0]
                            ? ({
                                '--font-family': `'${selectedFontPairing[0].style.font_family}'`,
                                '--font-weight': titleFontWeight,
                                '--font-style': selectedFontPairing[0].style.italic ? 'italic' : 'normal',
                              } as CSSProperties)
                            : undefined
                        }
                      >
                        {selectedFontPairing?.[0] ? (
                          selectedFontPairing[0].style.font_family
                        ) : (
                          <span>Loading Font...</span>
                        )}
                      </div>

                      <div
                        className={cx(Styles.fontSelectorLabel, { [Styles.placeholder]: !selectedFontPairing?.[1] })}
                        data-length={getFontNameLength(selectedFontPairing?.[1]?.style.font_family)}
                        style={
                          selectedFontPairing?.[1]
                            ? ({
                                '--font-family': `'${selectedFontPairing[1].style.font_family}'`,
                                '--font-weight': bodyFontWeight,
                                '--font-style': selectedFontPairing[1].style.italic ? 'italic' : 'normal',
                              } as CSSProperties)
                            : undefined
                        }
                      >
                        {selectedFontPairing?.[1] ? (
                          selectedFontPairing[1].style.font_family
                        ) : (
                          <span>Loading Font...</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={cx(Styles.sectionContainer, Styles.imagesContainer, {
                [Styles.minHeight]: (brandKit?.media_files && brandKit.media_files.length === 0) || isUploading,
              })}
            >
              <div className={Styles.header}>
                <div className={Styles.titleContainer}>
                  <span className={Styles.heading}>Images</span>
                </div>
                <Button
                  id="brand-kit-confirm-edit-images"
                  variant="ghost"
                  size="sm"
                  aria-label="Edit images"
                  frontIcon={Edit1}
                  onPress={() =>
                    handleOpenPreviewCardModal({
                      reviewImages: true,
                      onImagesChange: setPreviewBrandKit,
                    })
                  }
                >
                  Edit
                </Button>
              </div>
              {brandKit?.media_files && brandKit.media_files.length > 0 && (
                <div className={Styles.onlyUseMyBrandKitMediaContainer}>
                  <ToggleButton
                    size="sm-md"
                    checked={onlyUseBrandKitMedia}
                    name="only-use-brand-kit-media"
                    handleChange={handleToggleBrandKitMedia}
                  />
                  <Text variant="smallList">Only use my Brand Kit media</Text>
                  <Text variant="secondary" color="var(--dark-80)">
                    Stock media won't be used if you have images & video uploaded.
                  </Text>
                </div>
              )}
              <div
                className={cx(Styles.content, {
                  [Styles.imagesGrid]: isMobile && brandKit?.media_files && brandKit.media_files.length > 0,
                })}
              >
                {brandKit?.media_files && brandKit.media_files.length > 0 ? (
                  brandKit.media_files.slice(0, isMobile ? 6 : 7).map((media, index) => {
                    const hasOverlay =
                      brandKit.media_files &&
                      brandKit.media_files.length - (isMobile ? 6 : 7) > 0 &&
                      index === (isMobile ? 5 : 6)

                    return (
                      <div
                        className={cx(Styles.imageContainer, { [Styles.clickable]: hasOverlay })}
                        key={media.id}
                        onClick={
                          hasOverlay
                            ? () =>
                                handleOpenPreviewCardModal({
                                  reviewImages: true,
                                  onImagesChange: setPreviewBrandKit,
                                })
                            : undefined
                        }
                        style={hasOverlay ? { cursor: 'pointer' } : undefined}
                      >
                        <img src={media.thumbnail_url || media.source_url} alt={media.id} />
                        {hasOverlay && (
                          <div className={Styles.imageCounter}>
                            <Heading color="var(--light-100)" level={4}>
                              +{brandKit.media_files.length - (isMobile ? 6 : 7)}
                            </Heading>
                          </div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <Dropzone
                    onUpload={(files) => uploadImageFiles(files, undefined, { brand_kit_id: brandKit?.id })}
                    isLoading={isUploading}
                    displayText="Add more images so we can automatically add it into your content!"
                    buttonText="Add Media"
                  />
                )}
              </div>
            </div>
            <div className={cx(Styles.brandVoiceContainer, Styles.sectionContainer)}>
              <div className={Styles.header}>
                <div className={Styles.titleContainer}>
                  <span className={Styles.heading}>Brand Voice</span>
                </div>
                <Button
                  id="brand-kit-confirm-edit-brand-voice"
                  variant="ghost"
                  size="sm"
                  aria-label="Edit brand voice"
                  frontIcon={Edit1}
                  onPress={() =>
                    handleOpenPreviewCardModal({
                      reviewBrandVoice: true,
                    })
                  }
                >
                  Edit
                </Button>
              </div>
              <div className={Styles.content}>
                <Text lineClamp={2}>
                  {brandKit?.brand_voice?.voice_params?.purpose?.join(', ') || 'Lorem ipsum dolor sit amet'}
                </Text>
              </div>
            </div>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  },
)
